import { useState, useEffect, type FC } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { uploadDocument } from "@/shared/utils/uploadDocument";
import { EventType, trackEvent } from "@/shared/utils/core";

interface DeathCertModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  estateId: number;
  firstName: string;
  onUploaded: () => void;
  onSkip: () => void;
}

export const DeathCertModal: FC<DeathCertModalProps> = ({
  isOpen,
  onOpenChange,
  estateId,
  firstName,
  onUploaded,
  onSkip,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "loading" | "complete" | "error">(
    "idle",
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setUploadStatus("idle");
      setUploadError(null);
    }
  }, [isOpen]);

  const handleUpload = async () => {
    if (!file || uploadStatus === "loading") return;

    setUploadStatus("loading");
    setUploadError(null);

    try {
      await uploadDocument(file, "death_certificate", estateId);
      setUploadStatus("complete");
      trackEvent(EventType.ONBOARDING_DOCUMENT_UPLOADED, { document_type: "death_certificate" });
      onUploaded();
    } catch {
      setUploadStatus("error");
      setUploadError("Failed to upload document. Please try again.");
    }
  };

  const handleFileRemove = () => {
    setFile(null);
    setUploadStatus("idle");
    setUploadError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            <h3 className="text-xl font-semibold">One document needed before we can submit this</h3>
          </DialogTitle>
          <p className="text-sm text-secondary mt-2">
            To contact agencies and institutions in {firstName}'s name, we need a copy of the death
            certificate. Upload it here and we'll take it from there.
          </p>
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-2">
            <UploadZone
              label="Death certificate"
              file={file}
              onFileSelect={setFile}
              onFileRemove={handleFileRemove}
              fileCardStatus={uploadStatus}
              fileCardErrorMessage={uploadError ?? undefined}
            />

            <p className="text-2xs text-tertiary text-center">
              Any files uploaded will be encrypted and stored securely in your Documents folder.
            </p>
          </div>
        </DialogBody>

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onSkip} disabled={uploadStatus === "loading"}>
            I don't have it yet
          </Button>
          <Button
            variant="brand"
            onClick={handleUpload}
            disabled={!file || uploadStatus === "loading"}
          >
            {uploadStatus === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
